import { FinishedRouteAlert } from "@/reducers/messagesSlice"
import { badgeVariants } from "@/components/ui/badge"
import { formatDuration } from "@/components/RouteStopView"
import { CircleCheck, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

/**
 * The finished-order alert folded to one line — which order, whether it took
 * longer than planned, and how far and how long it actually ran. Clicking it
 * unfolds the full message: gps's text, the document and the
 * Expected/Actual/Approach table. Behind the `finish-alert-collapsed` flag.
 */
export function FinishAlertSummary({
  alert,
  onExpand,
}: {
  alert: FinishedRouteAlert
  onExpand: () => void
}) {
  const { t } = useTranslation()
  const { expectedDuration, actualDuration, actualDistance, transportNumber } =
    alert
  // No route version planned both stops — nothing to be late against.
  const hasPlan = Number.isFinite(expectedDuration) && expectedDuration > 0
  const overrun = actualDuration - expectedDuration
  const onTime = overrun <= 0

  const stats = [
    actualDistance ? `${Math.round(actualDistance / 1000)} km` : null,
    Number.isFinite(actualDuration) ? formatDuration(actualDuration) : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={false}
      title={t("maps.finishAlertSummary.expand")}
      data-testid="finish-alert-summary"
      className="flex w-full min-w-0 items-center gap-2 rounded-sm text-left text-sm hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <CircleCheck className="size-4 shrink-0 text-green-600" aria-hidden />
      <span className="truncate font-medium">
        {transportNumber
          ? t("maps.finishAlertSummary.finished", { number: transportNumber })
          : t("maps.finishAlertSummary.finishedNoNumber")}
      </span>
      {hasPlan && (
        <span
          className={cn(
            badgeVariants({ variant: onTime ? "positive" : "error" }),
            "shrink-0",
          )}
        >
          {onTime
            ? t("maps.finishAlertSummary.onTime")
            : // formatDuration reads anything under a minute as "seconds".
              `+${overrun < 60 ? "<1m" : formatDuration(overrun)}`}
        </span>
      )}
      {stats && <span className="shrink-0 text-muted-foreground">{stats}</span>}
      <ChevronDown
        className="ml-auto size-4 shrink-0 text-muted-foreground"
        aria-hidden
      />
    </button>
  )
}
